import { faker } from '@faker-js/faker';
import {
  CarrierCompany,
  CarrierContact,
  DispatcherContact,
  DispatcherInput,
} from '@cnx-saas/carrier-management';
import {
  fakeAddressFactory,
  fakeBaseFactory,
  fakeContactFactory,
  fakeOpeningHoursFactory,
  fakeRouteFactory,
  fakeTimeWindowFactory,
} from '@cnx-saas/common';
import {
  carrierId,
  genericId,
  loadId,
  stopId,
  tenantId,
  userId,
  vehicleId,
} from '@cnx-saas/utils-cnx-id';
import {
  Shipment,
  ShipmentAssignment,
  ShipmentAssignmentPayload,
  ShipmentDriver,
  ShipmentEventLog,
  ShipmentEventName,
  ShipmentPayload,
  ShipmentStatus,
  ShipmentStop,
  ShipmentStopCompletion,
  ShipmentStopDeliveryInFull,
  ShipmentStopEstimatedTimes,
  ShipmentStopLoad,
  ShipmentStopLocation,
  ShipmentStopStatus,
  ShipmentTransportRequirements,
  ShipmentVehicle,
  Shipper,
  VehicleCapability,
} from '../types';
import { ShipmentStopPlannedTimes } from '../types/ShipmentStopPlannedTimes';

export const fakeShipmentFactory = {
  createShipmentInput: (
    shipment: Partial<ShipmentPayload> = {},
  ): ShipmentPayload => ({
    stops: shipment.stops ?? [
      {
        location: fakeShipmentFactory.createShipmentStopLocation({
          address: fakeAddressFactory.createRealAddress(0),
        }),
        timeWindow: fakeTimeWindowFactory.createTimeWindow(),
        pickUp: [
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 10,
            weight: 10000,
          }),
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 20,
            weight: 20000,
          }),
        ],
        dropOff: [],
      },
      {
        location: fakeShipmentFactory.createShipmentStopLocation({
          address: fakeAddressFactory.createRealAddress(1),
        }),
        timeWindow: fakeTimeWindowFactory.createTimeWindow(),
        pickUp: [
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 20,
            weight: 20000,
          }),
        ],
        dropOff: [
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 10,
            weight: 10000,
          }),
        ],
      },
      {
        location: fakeShipmentFactory.createShipmentStopLocation({
          address: fakeAddressFactory.createRealAddress(2),
        }),
        timeWindow: fakeTimeWindowFactory.createTimeWindow(),
        pickUp: [],
        dropOff: [
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 20,
            weight: 20000,
          }),
          fakeShipmentFactory.createShipmentStopLoad({
            pallets: 20,
            weight: 20000,
          }),
        ],
      },
    ],
    distance:
      shipment.distance ?? faker.datatype.number({ min: 100, max: 1000 }),
    vehicle: shipment.vehicle ?? fakeShipmentFactory.createShipmentVehicle(),
    ...shipment,
  }),

  createShipment: (shipment: Partial<Shipment> = {}): Shipment => ({
    ...fakeBaseFactory.createBaseSchema(),
    eventLog: fakeShipmentFactory.createShipmentEventLog(),
    tenantId: tenantId(),
    flagged: shipment.flagged ?? faker.datatype.boolean(),
    status: shipment.status ?? ShipmentStatus.CREATED,
    stops: shipment.stops ?? fakeShipmentFactory.createShipmentStops(),
    transportRequirements:
      shipment.transportRequirements ??
      fakeShipmentFactory.createShipmentTransportRequirements(),
    incidents: [],
    distance:
      shipment.distance ?? faker.datatype.number({ min: 100, max: 1000 }),
    vehicle: shipment.vehicle ?? fakeShipmentFactory.createShipmentVehicle(),
    route: shipment.route ?? fakeRouteFactory.fakeRoute(),
    ...shipment,
  }),

  createShipments: (max = 10): Shipment[] =>
    [...Array(faker.datatype.number({ min: 1, max }))].map(() =>
      fakeShipmentFactory.createShipment(),
    ),

  createShipmentStop: (
    shipmentStop: Partial<ShipmentStop> = {},
  ): ShipmentStop => ({
    id: stopId(),
    location:
      shipmentStop.location ?? fakeShipmentFactory.createShipmentStopLocation(),
    timeWindow:
      shipmentStop.timeWindow ?? fakeTimeWindowFactory.createTimeWindow(),
    pickUp:
      shipmentStop.pickUp ?? fakeShipmentFactory.createShipmentStopLoads(),
    dropOff:
      shipmentStop.dropOff ?? fakeShipmentFactory.createShipmentStopLoads(),
    status: ShipmentStopStatus.IN_PROCESS,
    ...shipmentStop,
  }),

  createShipmentStops: (): ShipmentStop[] => [
    fakeShipmentFactory.createShipmentStop({
      pickUp: [
        fakeShipmentFactory.createShipmentStopLoad({
          pallets: 10,
          weight: 10000,
        }),
        fakeShipmentFactory.createShipmentStopLoad({
          pallets: 20,
          weight: 20000,
        }),
      ],
      dropOff: [],
      status: ShipmentStopStatus.IN_PROCESS,
    }),
    fakeShipmentFactory.createShipmentStop({
      pickUp: [
        fakeShipmentFactory.createShipmentStopLoad({
          pallets: 20,
          weight: 20000,
        }),
      ],
      dropOff: [
        fakeShipmentFactory.createShipmentStopLoad({
          pallets: 10,
          weight: 10000,
        }),
      ],
      status: ShipmentStopStatus.IN_PROCESS,
    }),
    fakeShipmentFactory.createShipmentStop({
      pickUp: [],
      dropOff: [
        fakeShipmentFactory.createShipmentStopLoad({
          pallets: 40,
          weight: 40000,
        }),
      ],
      status: ShipmentStopStatus.IN_PROCESS,
    }),
  ],

  createShipmentStopLocation: (
    shipmentStopLocation: Partial<ShipmentStopLocation> = {},
  ): ShipmentStopLocation => ({
    address: shipmentStopLocation.address ?? fakeAddressFactory.createAddress(),
    company: shipmentStopLocation.company ?? faker.company.name(),
    loadingRequirements:
      shipmentStopLocation.loadingRequirements ??
      fakeShipmentFactory.createLoadingRequirements(),
    serviceTime: faker.datatype.number({ max: 59 }),
    contact:
      shipmentStopLocation.contact ??
      fakeContactFactory.createContact({
        role: 'Dispatcher',
      }),
    openingHours: fakeOpeningHoursFactory.createOpeningHours(),
    ...shipmentStopLocation,
  }),

  createShipmentStopLoad: (
    shipmentStopLoad: Partial<ShipmentStopLoad> = {},
  ): ShipmentStopLoad => ({
    referenceId: loadId(),
    customerReference: faker.random.word(),
    pallets: faker.datatype.number({ min: 1, max: 100 }),
    weight: faker.datatype.number({ min: 1, max: 1000 }),
    ldmm: faker.datatype.number({ min: 1, max: 1000 }),
    ...shipmentStopLoad,
  }),

  createShipmentStopLoads: (max = 10) => {
    const size = faker.datatype.number({ min: 1, max });
    return [...Array(size)].map(() =>
      fakeShipmentFactory.createShipmentStopLoad(),
    );
  },

  createShipmentTransportRequirements: (
    transportRequirements: Partial<ShipmentTransportRequirements> = {},
  ): ShipmentTransportRequirements => ({
    requiredPalletCapacity: faker.datatype.number({ min: 1, max: 1000 }),
    requiredWeightCapacity: faker.datatype.number({ min: 1, max: 100000 }),
    loadingRequirements: fakeShipmentFactory.createLoadingRequirements(),
    ...transportRequirements,
  }),

  createLoadingRequirements: (): VehicleCapability[] =>
    Object.values(VehicleCapability).filter(() => faker.datatype.boolean()),

  createShipmentEventLog: (): ShipmentEventLog[] => [
    {
      id: genericId(),
      userId: userId(),
      tenantId: tenantId(),
      timestamp: new Date(),
      eventName: ShipmentEventName.SHIPMENT_STATUS_CREATED,
      status: ShipmentStatus.CREATED,
    },
  ],

  createCompany: (data?: Partial<CarrierCompany>): CarrierCompany => {
    return {
      name: faker.company.name(),
      address: fakeAddressFactory.createAddressWithoutCoordinates(),
      contact: fakeShipmentFactory.createContact(),
      ...data,
    };
  },

  createContact: (data?: Partial<CarrierContact>): CarrierContact => {
    return {
      email: faker.internet.email(),
      mobile: faker.phone.number(),
      name: faker.name.fullName(),
      phone: faker.phone.number(),
      ...data,
    };
  },

  createDispatcherContact: (
    data?: Partial<DispatcherContact>,
  ): DispatcherContact => {
    return {
      email: faker.internet.email(),
      mobile: faker.phone.number(),
      name: faker.name.fullName(),
      phone: faker.phone.number(),
      ...data,
    };
  },

  createCarrierInput: (): CarrierCompany => ({
    name: faker.company.name(),
    address: fakeAddressFactory.createAddressWithoutCoordinates(),
    contact: fakeShipmentFactory.createContact(),
  }),

  createCarrierDispatcher: (): DispatcherInput => ({
    contact: fakeShipmentFactory.createDispatcherContact(),
  }),

  createShipmentAssignmentInput: (): ShipmentAssignmentPayload => ({
    carrier: fakeShipmentFactory.createCarrierInput(),
    referenceId: carrierId(),
  }),

  createShipmentAssignment: (): ShipmentAssignment => ({
    ...fakeShipmentFactory.createShipmentAssignmentInput(),
    assignedAt: new Date(),
  }),

  createShipmentComment: (): string => faker.lorem.text(),

  createShipmentReferenceId: (): string => genericId(),

  createShipmentStopEstimatedTimes: (): ShipmentStopEstimatedTimes => ({
    estimatedArrivalTime: new Date(),
    estimatedDepartureTime: new Date(),
    comment: faker.lorem.text(),
  }),

  createShipmentStopCompletion: (): ShipmentStopCompletion => ({
    actualDepartureTime: new Date(),
    inFull: ShipmentStopDeliveryInFull.YES,
    comment: faker.lorem.text(),
  }),

  createShipmentVehicle: (): ShipmentVehicle => ({
    referenceId: vehicleId(),
    vehicleType: faker.random.word(),
    vehicleLicense: faker.random.word(),
    trailerLicense: faker.random.word(),
  }),

  createShipmentDriver: (): ShipmentDriver => ({
    name: faker.name.fullName(),
    phone: faker.phone.number(),
    email: faker.internet.email(),
  }),

  createShipmentStopPlannedTimes: (): ShipmentStopPlannedTimes => ({
    plannedArrivalTime: new Date(),
    plannedDepartureTime: new Date(),
  }),

  createShipper: (): Shipper => ({
    id: userId(),
    name: faker.company.name(),
    address: fakeAddressFactory.createAddressWithoutCoordinates(),
    contact: fakeShipmentFactory.createContact(),
    language: faker.random.locale(),
    taxId: faker.random.alphaNumeric(10),
  }),
};

export const fakeShipment: Shipment = fakeShipmentFactory.createShipment();
